// src/components/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiInstagram, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import { FaTiktok, FaFacebookF } from "react-icons/fa";

import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export const CONTACT = {
  company: "Kaida Beauty Studio SH.P.K.",
  address: "Rr. Dardania 28/3, 10000 Fushë Kosovë, Kosovë",
  phone: "+383 49 256 167",
  phoneHref: "+38349256167",
  email: "kaidabeautystudio@gmail.com",
  instagram: "kaidabeautystudio",
  instagramUrl: "https://instagram.com/kaidabeautystudio",
  // Paste the full profile links here — an icon only shows once its link is set.
  tiktokUrl: "",
  facebookUrl: "",
};

const SOCIALS = [
  { url: CONTACT.instagramUrl, label: "Instagram", Icon: FiInstagram },
  { url: CONTACT.tiktokUrl, label: "TikTok", Icon: FaTiktok },
  { url: CONTACT.facebookUrl, label: "Facebook", Icon: FaFacebookF },
].filter((s) => s.url);

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useLang();

  return (
    <Wrap>
      <Inner>
        <Top>
          <Brand to="/">
            <BrandName>Kaida</BrandName>
            <BrandSub>Beauty Studio</BrandSub>
          </Brand>

          <Links aria-label={t("footer.pages")}>
            <li>
              <FooterLink to="/">{t("nav.home")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/produktet">{t("nav.products")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/#trajnime">{t("nav.trainings")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/#sherbime">{t("nav.services")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/rreth-nesh">{t("nav.about")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/kontakt">{t("nav.contact")}</FooterLink>
            </li>
          </Links>

          <Right>
            {SOCIALS.length > 0 && (
              <Socials>
                {SOCIALS.map(({ url, label, Icon }) => (
                  <li key={label}>
                    <Social
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                    >
                      <Icon />
                    </Social>
                  </li>
                ))}
              </Socials>
            )}
            <Divider aria-hidden="true" />
            <LanguageSwitcher $text />
          </Right>
        </Top>

        <ContactRow>
          <Ext href={`tel:${CONTACT.phoneHref}`}>
            <FiPhone /> {CONTACT.phone}
          </Ext>
          <Ext href={`mailto:${CONTACT.email}`}>
            <FiMail /> {CONTACT.email}
          </Ext>
          <Plain>
            <FiMapPin /> {CONTACT.address}
          </Plain>
        </ContactRow>

        <Bottom>
          <span>
            © {year} Kaida Beauty Studio. {t("footer.rights")}
          </span>
          <span>
            {t("footer.builtBy")}{" "}
            <a href="https://asitesagency.com" target="_blank" rel="noreferrer">
              ASites Agency
            </a>
          </span>
        </Bottom>
      </Inner>
    </Wrap>
  );
}

/* ------------------------------------------------------------------ */

const Wrap = styled.footer`
  background: ${({ theme }) => theme.color.bgCream};
  border-top: 1px solid ${({ theme }) => theme.color.lineWarm};
  color: ${({ theme }) => theme.color.body};
  padding: 44px ${({ theme }) => theme.layout.gutter} 0;
`;

const Inner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 32px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
    justify-items: start;
    gap: 26px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  flex-direction: column;
  line-height: 1;
`;

const BrandName = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.45rem;
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  letter-spacing: -0.03em;
`;

const BrandSub = styled.span`
  font-size: 0.55rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  margin-top: 5px;
`;

const Links = styled.ul`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px clamp(18px, 2.4vw, 36px);

  ${({ theme }) => theme.down.lg} {
    justify-content: flex-start;
  }
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.inkSoft};
  transition: color 0.25s;

  &:hover {
    color: ${({ theme }) => theme.color.brand};
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const Socials = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Social = styled.a`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.color.ink};
  font-size: 1.05rem;
  transition:
    background 0.25s,
    color 0.25s;

  &:hover {
    background: ${({ theme }) => theme.color.bgBlush};
    color: ${({ theme }) => theme.color.brand};
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 26px;
  background: ${({ theme }) => theme.color.lineWarm};
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
  margin-top: 30px;
`;

const Ext = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  transition: color 0.25s;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.brand};
  }

  &:hover {
    color: ${({ theme }) => theme.color.ink};
  }
`;

const Plain = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.brand};
  }
`;

const Bottom = styled.div`
  margin-top: 22px;
  padding: 20px 0 26px;
  border-top: 1px solid ${({ theme }) => theme.color.lineWarm};
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.color.muted};

  a {
    color: ${({ theme }) => theme.color.inkSoft};
    transition: color 0.25s;
  }
  a:hover {
    color: ${({ theme }) => theme.color.brand};
  }

  ${({ theme }) => theme.down.sm} {
    flex-direction: column;
  }
`;
